/**
 * Badge Rarity Moderation Table Component
 * Lists collection badges and lets a moderator/admin set their rarity.
 */

import { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  MilitaryTech as RarityIcon,
} from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { SearchField } from '@components/common/SearchField'
import {
  useCollectionBadgesForModeration,
  useSetBadgeRarity,
} from '../../hooks/useBadgeCategories'
import { BADGE_RARITIES } from '@api/types/badgeCategory'
import type {
  BadgeRarity,
  CollectionBadgeModerationDto,
} from '@api/types/badgeCategory'

// Rarity color map (keep consistent with PlaySite: Common gray / Rare blue / Epic purple / Legendary gold)
const RARITY_COLORS: Record<BadgeRarity, string> = {
  Common: '#9e9e9e',
  Rare: '#2196f3',
  Epic: '#9c27b0',
  Legendary: '#ffc107',
}

function RarityChip({ rarity }: { rarity: BadgeRarity }) {
  return (
    <Chip
      label={rarity}
      size="small"
      sx={{
        backgroundColor: RARITY_COLORS[rarity],
        color: rarity === 'Legendary' ? 'rgba(0,0,0,0.87)' : '#fff',
        fontWeight: 600,
      }}
    />
  )
}

export function BadgeRarityModerationTable() {
  const [search, setSearch] = useState('')
  const [onlyPending, setOnlyPending] = useState(false)

  const [rarityDialogOpen, setRarityDialogOpen] = useState(false)
  const [selectedBadge, setSelectedBadge] = useState<CollectionBadgeModerationDto | null>(null)
  const [pendingRarity, setPendingRarity] = useState<BadgeRarity>('Common')

  // Paging state lives here; DataTable never owns it.
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data: badges, isLoading, error, refetch } = useCollectionBadgesForModeration({
    search: search || undefined,
    onlyPending,
  })
  const setRarityMutation = useSetBadgeRarity()
  const mobileDialogProps = useMobileDialogProps()

  const handleOpenRarityDialog = (badge: CollectionBadgeModerationDto) => {
    setSelectedBadge(badge)
    setPendingRarity(badge.rarity)
    setRarityDialogOpen(true)
  }

  const handleCloseRarityDialog = () => {
    setRarityDialogOpen(false)
    setSelectedBadge(null)
  }

  const handleSaveRarity = async () => {
    if (!selectedBadge) return
    try {
      await setRarityMutation.mutateAsync({
        badgeId: selectedBadge.id,
        data: { rarity: pendingRarity },
      })
      handleCloseRarityDialog()
    } catch {
      // Error surfaced via mutation state below
    }
  }

  const columns = useMemo<DataTableColumn<CollectionBadgeModerationDto>[]>(
    () => [
      {
        key: 'badge',
        header: 'Badge',
        primary: true,
        render: (badge) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={badge.imageUrl || undefined}
              variant="rounded"
              sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}
            >
              <RarityIcon />
            </Avatar>
            <Typography variant="body2" fontWeight="medium">
              {badge.name}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'collection',
        header: 'Collection',
        render: (badge) => <Typography variant="body2">{badge.collectionName}</Typography>,
      },
      {
        key: 'creator',
        header: 'Creator',
        hideBelow: 'md',
        render: (badge) => <Typography variant="body2">{badge.creatorDisplayName}</Typography>,
      },
      {
        key: 'category',
        header: 'Category',
        hideBelow: 'md',
        render: (badge) =>
          badge.categoryName || (
            <Typography component="span" variant="body2" color="text.secondary">
              None
            </Typography>
          ),
      },
      {
        key: 'rarity',
        header: 'Rarity',
        render: (badge) => <RarityChip rarity={badge.rarity} />,
      },
    ],
    []
  )

  return (
    <Box>
      {setRarityMutation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {setRarityMutation.error instanceof Error
            ? setRarityMutation.error.message
            : 'Failed to update rarity'}
        </Alert>
      )}

      <DataTable
        rows={badges ?? []}
        columns={columns}
        getRowKey={(badge) => badge.id}
        loading={isLoading}
        error={
          error ? (error instanceof Error ? error.message : 'Failed to load collection badges') : null
        }
        emptyMessage="No collection badges found"
        // No `total` => DataTable pages the rows it was given.
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 25, 50]}
        onRetry={() => refetch()}
        getRowActions={(badge) => [
          {
            key: 'rarity',
            label: 'Set rarity',
            icon: <RarityIcon fontSize="small" />,
            onClick: () => handleOpenRarityDialog(badge),
          },
        ]}
        toolbar={
          <>
            <SearchField
              value={search}
              onSearch={(q) => {
                setSearch(q)
                setPage(0) // the result set changes, so the current page may not exist
              }}
              placeholder="Search by badge or collection"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyPending}
                  onChange={(e) => {
                    setOnlyPending(e.target.checked)
                    setPage(0)
                  }}
                />
              }
              label="Only pending (Common)"
            />
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={() => refetch()} disabled={isLoading} aria-label="Refresh">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      />

      {/* Set Rarity Dialog */}
      <Dialog
        open={rarityDialogOpen}
        onClose={handleCloseRarityDialog}
        maxWidth="xs"
        fullWidth
        {...mobileDialogProps}
      >
        <DialogTitle>Set Badge Rarity</DialogTitle>
        <DialogContent>
          {selectedBadge && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  src={selectedBadge.imageUrl || undefined}
                  variant="rounded"
                  sx={{ width: 48, height: 48, bgcolor: 'primary.light' }}
                >
                  <RarityIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {selectedBadge.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedBadge.collectionName}
                  </Typography>
                </Box>
              </Box>
              <FormControl fullWidth>
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={pendingRarity}
                  label="Rarity"
                  onChange={(e) => setPendingRarity(e.target.value as BadgeRarity)}
                >
                  {BADGE_RARITIES.map((r) => (
                    <MenuItem key={r} value={r}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            backgroundColor: RARITY_COLORS[r],
                          }}
                        />
                        {r}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRarityDialog} disabled={setRarityMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRarity}
            disabled={setRarityMutation.isPending}
            startIcon={setRarityMutation.isPending ? <CircularProgress size={20} /> : null}
          >
            {setRarityMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
